import { useCallback, useState } from 'react'
import { Button, Input, InputNumber, Space, Tag, Typography, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, PAGE_SIZE, type PageResult } from '../api'
import type { SourceRef } from './ChatSourceRefList'

const { Text } = Typography

export type GoldDoc = {
  item_id: number
  title?: string
  grade?: number
}

type KnowledgeItemRow = {
  id: number
  title: string
  scope: string
}

type Props = {
  recordId: number
  scope: string
  goldDocs: GoldDoc[]
  topK: number
  chunkRefs?: SourceRef[] | null
  onSaved: (next: { eval_gold_docs: GoldDoc[]; eval_top_k: number; gold_ready: boolean }) => void
}

export function GoldDocEditor({ recordId, scope, goldDocs, topK, chunkRefs, onSaved }: Props) {
  const [docs, setDocs] = useState<GoldDoc[]>(goldDocs)
  const [k, setK] = useState(topK)
  const [saving, setSaving] = useState(false)
  const [searchQ, setSearchQ] = useState('')
  const [searchHits, setSearchHits] = useState<KnowledgeItemRow[]>([])
  const [searching, setSearching] = useState(false)

  const addDoc = (item: { item_id: number; title?: string }) => {
    if (docs.some((d) => d.item_id === item.item_id)) return
    setDocs((prev) => [...prev, { item_id: item.item_id, title: item.title || '', grade: 1 }])
  }

  const removeDoc = (itemId: number) => {
    setDocs((prev) => prev.filter((d) => d.item_id !== itemId))
  }

  const save = () => {
    setSaving(true)
    adminApi
      .patch(`/admin/retrieval-eval/cases/${recordId}/gold`, {
        eval_gold_docs: docs,
        eval_top_k: k,
      })
      .then((r) => {
        const data = r.data as {
          eval_gold_docs: GoldDoc[]
          eval_top_k: number
          gold_ready: boolean
        }
        setDocs(data.eval_gold_docs)
        setK(data.eval_top_k)
        onSaved(data)
        message.success('Gold 文档已保存')
      })
      .catch((e) => message.error(formatApiError(e)))
      .finally(() => setSaving(false))
  }

  const searchItems = useCallback(() => {
    const q = searchQ.trim()
    if (!q) {
      setSearchHits([])
      return
    }
    setSearching(true)
    adminApi
      .get<PageResult<KnowledgeItemRow>>('/knowledge/items', {
        params: { page: 1, page_size: PAGE_SIZE, q, scope },
      })
      .then((r) => setSearchHits(r.data.items))
      .catch(() => setSearchHits([]))
      .finally(() => setSearching(false))
  }, [searchQ, scope])

  const refCandidates = (chunkRefs || []).filter(
    (r, i, arr) => r.item_id && arr.findIndex((x) => x.item_id === r.item_id) === i,
  )

  return (
    <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
      <Text strong>检索评测 Gold 文档</Text>
      <Text type="secondary" style={{ display: 'block', marginTop: 4, marginBottom: 8, fontSize: 12 }}>
        标注期望命中的知识条目，用于 Recall / 排名 / nDCG 等指标（无需手写 JSON）。
      </Text>
      <Space wrap style={{ marginBottom: 8 }}>
        {docs.length ? (
          docs.map((d) => (
            <Tag
              key={d.item_id}
              closable
              onClose={() => removeDoc(d.item_id)}
              style={{ maxWidth: 360 }}
            >
              #{d.item_id} {d.title || ''}
            </Tag>
          ))
        ) : (
          <Text type="secondary">尚未设置</Text>
        )}
      </Space>
      {refCandidates.length ? (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            从回答参考来源添加：
          </Text>
          <Space wrap style={{ margin: '6px 0 10px' }}>
            {refCandidates.map((r) => (
              <Button
                key={r.item_id}
                size="small"
                icon={<PlusOutlined />}
                disabled={docs.some((d) => d.item_id === r.item_id)}
                onClick={() => addDoc({ item_id: r.item_id!, title: r.title })}
              >
                {r.title || `#${r.item_id}`}
              </Button>
            ))}
          </Space>
        </>
      ) : null}
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Input
          placeholder="按标题搜索知识库文档"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onPressEnter={searchItems}
        />
        <Button loading={searching} onClick={searchItems}>
          搜索
        </Button>
      </Space.Compact>
      {searchHits.length ? (
        <Space wrap style={{ marginBottom: 10 }}>
          {searchHits.map((row) => (
            <Button
              key={row.id}
              size="small"
              icon={<PlusOutlined />}
              disabled={docs.some((d) => d.item_id === row.id)}
              onClick={() => addDoc({ item_id: row.id, title: row.title })}
            >
              {row.title}
            </Button>
          ))}
        </Space>
      ) : null}
      <Space wrap>
        <span>
          Top K{' '}
          <InputNumber min={1} max={50} value={k} onChange={(v) => setK(v ?? 10)} size="small" />
        </span>
        <Button type="primary" size="small" loading={saving} onClick={save}>
          保存 Gold
        </Button>
      </Space>
    </div>
  )
}
